import styled from '@emotion/styled';
import {
	ReactNode,
	ComponentProps,
	InputEvent,
	forwardRef,
	PropsWithChildren,
} from 'react';
import { Css, ITheme } from '../../../ui';
import { Color } from '../../../styles/Color';

interface IProps extends Omit<ComponentProps<'input'>, 'placeholder'> {
	label: string;
	labelId?: string;
	icon?: ReactNode;
	isInvalid?: boolean;
	readonly?: boolean;
}

const StyledInput = styled.input<
	{ isInvalid: boolean; readonly: boolean },
	ITheme
>`
	caret-color: ${(props) => props.theme.color.brand};
	border-radius: 8px;
	padding: ${Css.pxToRem(28)} ${Css.pxToRem(12)} ${Css.pxToRem(8)}
		${Css.pxToRem(56)};
	padding-right: ${Css.pxToRem(12)};
	background: ${Color.InputBackground};
	font-size: ${Css.pxToRem(16)};
	color: ${(props) => props.theme.color.onPrimary};
	width: 100%;
	border: none;
	outline: 2px solid transparent;

	border-bottom: 2px solid
		${(props) => (props.isInvalid ? `${Color.Error}` : `transparent`)};

	&::placeholder {
		opacity: 0;
	}

	&:focus {
		color: ${(props) => props.theme.color.text.default};
		border-color: ${(props) => props.theme.color.brand};
	}
`;

const InputWrapper = styled.div`
	position: relative;
	width: 100%;
	overflow: hidden;
`;

const IconWrapper = styled.div`
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	padding: ${Css.pxToRem(16)};
	pointer-events: none;
	display: flex;
	align-items: center;

	svg path {
		fill: currentColor;
	}
`;

const Label = styled.label<{ isInvalid: boolean }, ITheme>`
	color: ${(props) => (props.isInvalid ? Color.Error : Color.Neutral6)};
	position: absolute;
	top: 0;
	right: ${Css.pxToRem(56)};
	left: ${Css.pxToRem(56)};
	height: 100%;
	font-size: ${Css.pxToRem(16)};
	font-weight: 400;
	display: flex;
	align-items: center;
	transition: all 0.15s ease-in-out;
	transform-origin: top left;
	pointer-events: none;
	overflow: hidden;

	input:focus ~ &,
	input:not(:placeholder-shown) ~ & {
		font-size: ${Css.pxToRem(12)};
		height: 60%;
	}
`;

const LabelTruncate = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
`;

const StyledIconWrapper = styled(IconWrapper)<{ isInvalid: boolean }, ITheme>`
	color: ${(props) =>
		props.isInvalid ? Color.Error : props.theme.color.secondaryLight};
`;

export const Input = forwardRef<HTMLInputElement, PropsWithChildren<IProps>>(
	(
		{
			label,
			labelId,
			id,
			readonly = false,
			children,
			icon,
			isInvalid = false,
			...inputProps
		},
		ref,
	) => (
		<InputWrapper>
			<StyledInput
				ref={ref}
				type="text"
				id={id}
				aria-labelledby={labelId}
				placeholder={label}
				isInvalid={isInvalid}
				readonly={readonly}
				onBeforeInput={
					readonly
						? (e: InputEvent<HTMLInputElement>) => {
								e.preventDefault();
						  }
						: undefined
				}
				{...inputProps}
			/>
			<Label htmlFor={id} id={labelId} isInvalid={isInvalid}>
				<LabelTruncate>{label}</LabelTruncate>
			</Label>
			<StyledIconWrapper isInvalid={isInvalid}>{icon}</StyledIconWrapper>
			{children}
		</InputWrapper>
	),
);
